import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { CreateUserRequest, Partner } from '../../types/user-management';
import { logger } from '../../utils';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserRequest) => Promise<void>;
  partners: Partner[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  partners,
}) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    name: '',
    role: 'tedy-partner',
    organization: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        email: '',
        name: '',
        role: 'tedy-partner',
        organization: '',
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (formData.role === 'tedy-partner' && !formData.organization) {
      newErrors.organization = 'Organization is required for partners';
    }

    if (formData.role === 'tedy-student' && !formData.partnerId) {
      newErrors.partnerId = 'Partner assignment is required for students';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
      onClose();
    } catch (error) {
      logger.error('ADMIN', 'Failed to create user', { error });
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to create user. Please try again.',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-tedy-black">Create User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    role: e.target.value as 'tedy-partner' | 'tedy-student',
                    // Clear organization when switching to student
                    organization: e.target.value === 'tedy-student' ? '' : prev.organization,
                    // Clear partnerId when switching to partner
                    partnerId: e.target.value === 'tedy-partner' ? undefined : prev.partnerId,
                  }))
                }
                className="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-tedy-red focus:ring-tedy-red"
              >
                <option value="tedy-partner">Partner</option>
                <option value="tedy-student">Student</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-tedy-red focus:ring-tedy-red"
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-tedy-red focus:ring-tedy-red"
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {formData.role === 'tedy-partner' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization
                </label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      organization: e.target.value,
                    }))
                  }
                  className="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-tedy-red focus:ring-tedy-red"
                  placeholder="Enter organization name"
                />
                {errors.organization && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.organization}
                  </p>
                )}
              </div>
            )}

            {formData.role === 'tedy-student' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to Partner
                </label>
                <select
                  value={formData.partnerId || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      partnerId: e.target.value || undefined,
                    }))
                  }
                  className="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-tedy-red focus:ring-tedy-red"
                >
                  <option value="">Select a partner</option>
                  {partners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.organization} - {partner.name}
                    </option>
                  ))}
                </select>
                {errors.partnerId && (
                  <p className="mt-1 text-sm text-red-600">{errors.partnerId}</p>
                )}
              </div>
            )}

            {errors.submit && (
              <div className="bg-red-50 text-red-600 p-4 rounded-md">
                {errors.submit}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
